<!-- form-quickbooks-settings.component.html -->
<mat-card class="configuration-card">
  <mat-card-content>
    <form [formGroup]="quickbooksSettingsForm" (ngSubmit)="onSubmit()">
      <mat-progress-spinner
        class="matProgressSpinnerCentered"
        *ngIf="showProgressSpinner"
        mode="indeterminate">
      </mat-progress-spinner>

      <!-- settingsLoaded keeps the disconnected hero from painting pre-load. -->
      <ng-container *ngIf="settingsLoaded && !showProgressSpinner">

        <!-- Connection status hero -->
        <div class="status-hero" [class.is-disconnected]="!isConnected">
          <div class="status-ring">
            <mat-icon>{{ isConnected ? 'check_circle' : 'error' }}</mat-icon>
          </div>
          <div class="status-text">
            <h2>{{ isConnected ? 'Connected to QuickBooks' : 'Not connected' }}</h2>
            <p *ngIf="isConnected">This studio is authorized and ready to sync.</p>
            <p *ngIf="!isConnected">Connect your QuickBooks company to start syncing.</p>
            <p *ngIf="!isConnected && !appConfigured && !loadingAccountInfo">
              QuickBooks isn't configured on the server yet. Contact support.
            </p>
          </div>
          <div class="status-actions">
            <button *ngIf="!isConnected && appConfigured"
                    mat-flat-button color="primary" type="button"
                    (click)="authorizeQuickbooks()">
              <mat-icon>vpn_key</mat-icon> Connect to QuickBooks
            </button>
          </div>
        </div>

        <!-- Inline error banner -->
        <div class="inline-banner banner-error" *ngIf="errorMessage">
          <mat-icon>error_outline</mat-icon>
          <div class="banner-body">
            <strong>Something went wrong</strong>
            <span>{{ errorMessage }}</span>
          </div>
        </div>

        <app-success-message [message]="successMessage"></app-success-message>

        <!-- Connected company details -->
        <div class="section" *ngIf="isConnected">
          <div class="section-head">
            <mat-icon class="section-icon">apartment</mat-icon>
            <div>
              <h3 class="section-title">Connected company</h3>
              <p class="section-sub" *ngIf="loadingAccountInfo">Loading company details…</p>
            </div>
          </div>
          <div class="row" *ngIf="accountInfo && !loadingAccountInfo">
            <div class="col-12 col-md-6 mb-2">
              <span class="text-muted small d-block">Company</span>
              <span>{{ accountInfo.company_name || '-' }}</span>
            </div>
            <div class="col-12 col-md-6 mb-2">
              <span class="text-muted small d-block">Realm ID</span>
              <span>{{ accountInfo.realm_id || '-' }}</span>
            </div>
            <div class="col-12 col-md-6 mb-2">
              <span class="text-muted small d-block">Environment</span>
              <span>{{ accountInfo.environment || '-' }}</span>
            </div>
            <div class="col-12 col-md-6 mb-2">
              <span class="text-muted small d-block">Access token expires</span>
              <span>{{ accountInfo.token_expires_at || '-' }}</span>
            </div>
            <div class="col-12 col-md-6 mb-2">
              <span class="text-muted small d-block">Refresh token expires</span>
              <span>{{ accountInfo.refresh_expires_at || '-' }}</span>
            </div>
          </div>
        </div>

        <!-- Enable toggle -->
        <div class="section enable-section">
          <mat-slide-toggle formControlName="qbEnabled" color="primary">
            Enable QuickBooks for this studio
          </mat-slide-toggle>
        </div>

        <!-- Account mapping -->
        <div class="section">
          <div class="section-head">
            <mat-icon class="section-icon">account_balance</mat-icon>
            <div>
              <h3 class="section-title">Account mapping</h3>
              <p class="section-sub">Which QuickBooks accounts we post to.</p>
            </div>
          </div>

          <div class="row">
            <div class="col-12 col-md-6">
              <mat-form-field appearance="outline" class="w-100">
                <mat-label>Income Account</mat-label>
                <input matInput formControlName="qbIncomeAccount" autocomplete="off">
              </mat-form-field>
            </div>

            <div class="col-12 col-md-6">
              <mat-form-field appearance="outline" class="w-100">
                <mat-label>Payments Account</mat-label>
                <input matInput formControlName="qbPaymentsAccount" autocomplete="off">
              </mat-form-field>
            </div>

            <div class="col-12 col-md-6">
              <mat-form-field appearance="outline" class="w-100">
                <mat-label>Discount Account</mat-label>
                <input matInput formControlName="qbDiscountAccount" autocomplete="off">
              </mat-form-field>
            </div>

            <div class="col-12 col-md-6">
              <mat-form-field appearance="outline" class="w-100">
                <mat-label>Interest Account</mat-label>
                <input matInput formControlName="qbInterestAccount" autocomplete="off">
              </mat-form-field>
            </div>
          </div>
        </div>

        <!-- Sync -->
        <div class="section" *ngIf="isConnected">
          <div class="section-head">
            <mat-icon class="section-icon">sync</mat-icon>
            <div>
              <h3 class="section-title">Sync</h3>
              <p class="section-sub">Send anything not yet in QuickBooks.</p>
            </div>
          </div>

          <div class="row">
            <div class="col-12 col-md-4 mb-2">
              <span class="text-muted small d-block">Customers waiting</span>
              <span>{{ unsyncedCustomers }}</span>
            </div>
            <div class="col-12 col-md-4 mb-2">
              <span class="text-muted small d-block">Invoices waiting</span>
              <span>{{ unsyncedInvoices }}</span>
            </div>
            <div class="col-12 col-md-4 mb-2">
              <span class="text-muted small d-block">Payments waiting</span>
              <span>{{ unsyncedPayments }}</span>
            </div>
            <div class="col-12 col-md-4 mb-2">
              <span class="text-muted small d-block">Items waiting to send</span>
              <span>{{ pendingItems }}</span>
            </div>
            <div class="col-12 col-md-4 mb-2">
              <span class="text-muted small d-block">Queue depth (all studios)</span>
              <span>{{ queueDepth }}</span>
            </div>
          </div>

          <div class="alert alert-warning" *ngIf="lastError">
            <strong>Last sync error:</strong> {{ lastError }}
          </div>

          <div class="d-flex align-items-center flex-wrap gap-2">
            <button mat-stroked-button type="button" (click)="syncNow()"
                    [disabled]="syncing || syncInProgress">
              <mat-icon>sync</mat-icon>
              {{ syncing ? 'Queuing…' : 'Sync Now' }}
            </button>
            <button mat-button type="button" (click)="loadStatus()" [disabled]="loadingStatus">
              <mat-icon>refresh</mat-icon>
              {{ loadingStatus ? 'Refreshing…' : 'Refresh Status' }}
            </button>
            <span class="text-muted" *ngIf="syncInProgress">
              Sync in progress: {{ pendingItems }} items remaining.
            </span>
            <span class="text-muted" *ngIf="syncMessage">{{ syncMessage }}</span>
          </div>

          <p class="text-muted small mb-0 mt-2" *ngIf="syncCounts.length">
            Queued:
            <span *ngFor="let item of syncCounts; let last = last">
              {{ item.count }} {{ item.label }}<span *ngIf="!last">, </span>
            </span>
          </p>
        </div>

        <!-- Primary action bar -->
        <div class="action-bar">
          <button mat-flat-button color="primary" type="submit">
            <mat-icon>save</mat-icon> Save Settings
          </button>
          <button *ngIf="isConnected" mat-stroked-button color="warn" type="button"
                  (click)="deauthorizeQuickbooks()">
            <mat-icon>link_off</mat-icon> Disconnect
          </button>
          <span class="action-spring"></span>
        </div>

      </ng-container>
    </form>
  </mat-card-content>
</mat-card>
